import * as z3 from 'zod/v3';
import * as z from 'zod/v4';
import { Expect } from '@watershed/shared-util/types/Expect';
import { AreTypesEqual } from '@watershed/shared-util/types/AreTypesEqual';

import { YM, YMInterval } from '../utils/YearMonth';

export const yearMonthSchemaZ3 = z3
  .number()
  .positive()
  .int()
  .transform(YM.fromNumber);
export const yearMonthSchema = z
  .number()
  .positive()
  .int()
  .transform(YM.fromNumber);
// TODO(steven.roberts, DEVEX-690): [Zod Upgrade] We had to bifurcate (e.g. copy/paste) some types to break up the zod migration
// Make sure to keep the schemas in this typecheck in sync! This type is intended to make sure they stay in sync it will error if there's drift
type _YearMonthSchemaTypesEqual = Expect<
  AreTypesEqual<
    z3.infer<typeof yearMonthSchemaZ3>,
    z.infer<typeof yearMonthSchema>
  >
>;

export const ymIntervalShapeSchemaZ3 = z3.object({
  start: yearMonthSchemaZ3,
  end: yearMonthSchemaZ3,
});
export const ymIntervalShapeSchema = z.object({
  start: yearMonthSchema,
  end: yearMonthSchema,
});
// TODO(steven.roberts, DEVEX-690): [Zod Upgrade] We had to bifurcate (e.g. copy/paste) some types to break up the zod migration
// Make sure to keep the schemas in this typecheck in sync! This type is intended to make sure they stay in sync it will error if there's drift
type _YMIntervalShapeSchemaTypesEqual = Expect<
  AreTypesEqual<
    z3.infer<typeof ymIntervalShapeSchemaZ3>,
    z.infer<typeof ymIntervalShapeSchema>
  >
>;

export const ymIntervalSchemaZ3 = ymIntervalShapeSchemaZ3.transform(
  YMInterval.fromObject
);
export const ymIntervalSchema = ymIntervalShapeSchema.transform(
  YMInterval.fromObject
);
// TODO(steven.roberts, DEVEX-690): [Zod Upgrade] We had to bifurcate (e.g. copy/paste) some types to break up the zod migration
// Make sure to keep the schemas in this typecheck in sync! This type is intended to make sure they stay in sync it will error if there's drift
type _YMIntervalSchemaTypesEqual = Expect<
  AreTypesEqual<
    z3.infer<typeof ymIntervalSchemaZ3>,
    z.infer<typeof ymIntervalSchema>
  >
>;

export const ymIntervalArrayZ3 = z3.array(ymIntervalSchemaZ3);
export const ymIntervalArray = z.array(ymIntervalSchema);
// TODO(steven.roberts, DEVEX-690): [Zod Upgrade] We had to bifurcate (e.g. copy/paste) some types to break up the zod migration
// Make sure to keep the schemas in this typecheck in sync! This type is intended to make sure they stay in sync it will error if there's drift
type _YMIntervalArrayTypesEqual = Expect<
  AreTypesEqual<
    z3.infer<typeof ymIntervalArrayZ3>,
    z.infer<typeof ymIntervalArray>
  >
>;

export const ymIntervalsSchema = z.array(ymIntervalSchema);

export const tcfdRiskSchemaZ3 = z3.object({
  id: z3.string(),
  title: z3.string(),
  userDescription: z3.string(),
  kind: z3.string(),
  physicalRiskSeverity: z3.string().optional(),
});
export const tcfdRiskSchema = z.object({
  id: z.string(),
  title: z.string(),
  userDescription: z.string(),
  kind: z.string(),
  physicalRiskSeverity: z.string().optional(),
});
type _TCFDRiskSchemaTypesEqual = Expect<
  AreTypesEqual<
    z3.infer<typeof tcfdRiskSchemaZ3>,
    z.infer<typeof tcfdRiskSchema>
  >
>;

export type TcfdRisk = z.infer<typeof tcfdRiskSchema>;

export const tcfdImpactSchema = z.object({
  shortTermImpact: z.string().optional(),
  mediumTermImpact: z.string().optional(),
  longTermImpact: z.string().optional(),
});

export const sfdrPaiSchema = z.object({
  key: z.string(),
  parentCategory: z.string().optional(),
  category: z.string(),
  subcategory: z.string(),
  metric: z.string(),
  required: z.boolean(),
  value: z.number().optional(),
});

export type SfdrPai = z.infer<typeof sfdrPaiSchema>;

export type TcfdImpact = z.infer<typeof tcfdImpactSchema>;

export const ymIntervalStringSchema = z
  .string()
  .transform(YMInterval.fromURLString);

export const dateWithFormatOptionsSchema = z.object({
  date: z.coerce.date(),
  options: z
    .object({
      weekday: z.enum(['long', 'short', 'narrow']).optional(),
      year: z.enum(['numeric', '2-digit']).optional(),
      // only allow alphabetical month formats to avoid
      // incorrectness across locales
      month: z.enum(['long', 'short', 'narrow']).optional(),
      day: z.enum(['numeric', '2-digit']).optional(),
      hour: z.enum(['numeric', '2-digit']).optional(),
      minute: z.enum(['numeric', '2-digit']).optional(),
    })
    .optional(),
});

// See note on supporting date strings in the zod documentation:
// https://github.com/colinhacks/zod#dates
export const dateSchema = z.preprocess((arg) => {
  if ((typeof arg === 'string' && arg.length > 0) || arg instanceof Date) {
    return new Date(arg);
  }
  return undefined;
}, z.date().optional());

export const emptyObjectSchema = z.record(z.string(), z.never());
export type EmptyObject = z.infer<typeof emptyObjectSchema>;

export function isEmptyObject(obj: object): obj is EmptyObject {
  const result = emptyObjectSchema.safeParse(obj);
  return result.success;
}
